import { relativeTime } from '../../lib/format.js'
import { saveText } from '../../lib/mail.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- calendar-feeds-dialog ---
let __fez_component_0;
{
const MAX_BYTES = 5 * 1024 * 1024
__fez_component_0 = class {
  PROPS = {
    container: { type: String, required: true },
    mailbox: { type: String, required: true },
  }

  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.url = null
    this.state.subscriptions = []
    this.state.busy = ''
    this.state.ask = ''
    this.state.renaming = ''
    this.state.errors = {}
    this.state.fileError = ''
  }

  onMount() {
    this.load()
  }

  request(action, params = {}, options = { silent: true }) {
    return window.App.api(`container/${this.props.container}/${action}`, { mailbox: this.props.mailbox, ...params }, options)
  }

  async load() {
    try {
      const [feed, list] = await Promise.all([this.request('calendar_feed'), this.request('calendar_subscriptions')])
      this.state.url = feed.url
      this.state.subscriptions = list.items
      this.state.error = ''
    } catch (error) {
      this.state.error = error.message
    } finally {
      this.state.loading = false
    }
  }

  // Publish, New address and Stop publishing; the last two are asked for once in place.
  async feed(action) {
    if (action !== 'calendar_feed_on' && this.state.ask !== action) {
      this.state.ask = action
      return
    }
    this.state.ask = ''
    this.state.busy = action
    try {
      this.state.url = (await this.request(action, {}, {})).url
    } finally {
      this.state.busy = ''
    }
  }

  unask() {
    this.state.ask = ''
  }

  async subscribe(event) {
    event.preventDefault()
    const form = event.target
    const values = this.formData(form)
    this.state.busy = 'subscribe'
    try {
      await this.request('calendar_subscription_add', { url: values.url, name: values.name || undefined }, {})
      this.state.errors = {}
      form.reset()
      await this.load()
    } catch (error) {
      this.state.errors = error.details || { url: error.message }
    } finally {
      this.state.busy = ''
    }
  }

  // A failed fetch still answers with the subscription (its `error` says why): warn, not cheer.
  async refresh(ref) {
    this.state.busy = `refresh:${ref}`
    try {
      const { data, message } = await this.request('calendar_subscription_refresh', { subscription: ref }, { silent: true, envelope: true })
      if (data.error) window.Toast.error(message)
      else window.Toast.success(message)
      await this.load()
    } catch (error) {
      window.Toast.error(error.message)
    } finally {
      this.state.busy = ''
    }
  }

  rename(ref) {
    this.state.renaming = ref
    this.state.errors = {}
  }

  async saveName(event) {
    event.preventDefault()
    const ref = this.state.renaming
    this.state.busy = `rename:${ref}`
    try {
      await this.request('calendar_subscription_rename', { subscription: ref, name: this.formData(event.target).name }, {})
      this.state.renaming = ''
      await this.load()
    } catch (error) {
      this.state.errors = error.details || { name: error.message }
    } finally {
      this.state.busy = ''
    }
  }

  unrename() {
    this.state.renaming = ''
  }

  async remove(ref) {
    if (this.state.ask !== `remove:${ref}`) {
      this.state.ask = `remove:${ref}`
      return
    }
    this.state.ask = ''
    this.state.busy = `remove:${ref}`
    try {
      await this.request('calendar_subscription_delete', { subscription: ref }, {})
      await this.load()
    } finally {
      this.state.busy = ''
    }
  }

  pick() {
    this.state.fileInput.click()
  }

  async chosen(event) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.size > MAX_BYTES) {
      this.state.fileError = 'The file is larger than 5 MB.'
      return
    }
    this.state.fileError = ''
    this.state.busy = 'import'
    try {
      await this.request('calendar_import', { data: await file.text() }, {})
    } catch (error) {
      this.state.fileError = error.details?.data || error.message
    } finally {
      this.state.busy = ''
    }
  }

  async download() {
    this.state.busy = 'download'
    try {
      const file = await this.request('calendar_download')
      saveText(file.ics, file.filename, 'text/calendar')
    } finally {
      this.state.busy = ''
    }
  }

  beforeRender() {
    this.state.rows = this.state.subscriptions.map((subscription) => ({
      ...subscription,
      meta: [`${subscription.events} event${subscription.events === 1 ? '' : 's'}`, subscription.fetched_at && `fetched ${relativeTime(subscription.fetched_at)}`].filter(Boolean).join(' · '),
      asking: this.state.ask === `remove:${subscription.ref}`,
      renaming: this.state.renaming === subscription.ref,
      refreshing: this.state.busy === `refresh:${subscription.ref}`,
      removing: this.state.busy === `remove:${subscription.ref}`,
    }))
    this.state.askText = { calendar_feed_rotate: 'Make a new address? The current one stops working, and apps using it stop updating.', calendar_feed_off: 'Stop publishing? The address stops working.' }[this.state.ask] || ''
  }

  CSS = `:fez {
display:block;.cf-section + .cf-section{margin-top:var(--space-6)}.cf-section h3{margin:0 0 var(--space-2);color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-bold);letter-spacing:0.06em;text-transform:uppercase}.cf-hint{margin:var(--space-2) 0;color:var(--color-text-muted);font-size:var(--text-sm)}.cf-actions{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2)}.cf-ask{display:grid;gap:var(--space-2);margin-top:var(--space-2);padding:var(--space-3);border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg-subtle);font-size:var(--text-sm)}.cf-sub{display:grid;gap:var(--space-2);padding:var(--space-2) 0;border-bottom:1px solid var(--color-border)}.cf-sub-head{display:flex;align-items:center;gap:var(--space-2);min-width:0}.cf-swatch{width:12px;height:12px;flex:none;border-radius:var(--radius-sm);background:var(--tag)}.cf-sub-text{display:grid;flex:1;min-width:0}.cf-sub-name{overflow:hidden;font-size:var(--text-sm);font-weight:var(--weight-medium);text-overflow:ellipsis;white-space:nowrap}.cf-sub-meta{overflow:hidden;color:var(--color-text-muted);font-size:var(--text-xs);text-overflow:ellipsis;white-space:nowrap}.cf-sub-error{color:var(--color-warning);font-size:var(--text-xs);overflow-wrap:anywhere}.cf-inline{display:flex;flex-wrap:wrap;align-items:flex-end;gap:var(--space-2)}.cf-grow{flex:1;min-width:200px}.cf-add{display:grid;gap:var(--space-3);margin-top:var(--space-3)}.cf-file-input{display:none}.cf-error{color:var(--color-danger);font-size:var(--text-sm)}.cf-note{display:grid;place-items:center;min-height:160px}
}`

  HTML = `{#if state.error}
<div class="cf-note"><ui-empty icon="warning-circle" title="Could not load" text={state.error}></ui-empty></div>
{:else if state.loading}
<div class="cf-note"><ui-spinner></ui-spinner></div>
{:else}
<section class="cf-section">
<h3>Publish</h3>
{#if state.url}
<ui-copy label="Secret address" value={state.url}></ui-copy>
<p class="cf-hint">Add it to Google Calendar, Apple Calendar or Outlook as a calendar from a URL. Anyone with it sees this mailbox's events, not the calendars it subscribes to.</p>
{#if state.askText}
<div class="cf-ask">
<span>{state.askText}</span>
<div class="cf-actions">
<ui-button size="sm" label="Cancel" onclick="fez.unask()"></ui-button>
<ui-button size="sm" variant="danger" label={state.ask === 'calendar_feed_off' ? 'Stop publishing' : 'New address'} onclick="fez.feed('{state.ask}')"></ui-button>
</div>
</div>
{:else}
<div class="cf-actions">
<ui-button size="sm" icon="arrows-clockwise" label="New address" loading={state.busy === 'calendar_feed_rotate'} onclick="fez.feed('calendar_feed_rotate')"></ui-button>
<ui-button size="sm" variant="danger-ghost" label="Stop publishing" loading={state.busy === 'calendar_feed_off'} onclick="fez.feed('calendar_feed_off')"></ui-button>
</div>
{/if}
{:else}
<p class="cf-hint">Publish this mailbox's calendar at a secret address that calendar apps subscribe to and keep up with.</p>
<ui-button size="sm" variant="primary" icon="link-simple" label="Publish" loading={state.busy === 'calendar_feed_on'} onclick="fez.feed('calendar_feed_on')"></ui-button>
{/if}
</section>

<section class="cf-section">
<h3>Subscriptions</h3>
{#each state.rows as row}
<div class="cf-sub" key={row.ref}>
{#if row.renaming}
<form class="cf-inline" onsubmit="fez.saveName(event)" novalidate>
<span class="cf-grow"><ui-input name="name" label="Name" value={row.name} maxlength="100" error={state.errors.name} autofocus></ui-input></span>
<ui-button label="Cancel" onclick="fez.unrename()"></ui-button>
<ui-button type="submit" variant="primary" label="Save" :loading="state.busy === 'rename:' + row.ref"></ui-button>
</form>
{:else}
<div class="cf-sub-head">
<span class="cf-swatch" style="--tag: var(--color-avatar-{row.color})"></span>
<span class="cf-sub-text">
<span class="cf-sub-name">{row.name}</span>
<span class="cf-sub-meta" title={row.url}>{row.meta}</span>
</span>
<ui-button size="sm" variant="ghost" icon="arrows-clockwise" title="Refresh" aria-label="Refresh" loading={row.refreshing} onclick="fez.refresh('{row.ref}')"></ui-button>
<ui-button size="sm" variant="ghost" icon="pencil-simple" title="Rename" aria-label="Rename" onclick="fez.rename('{row.ref}')"></ui-button>
<ui-button size="sm" variant="ghost" icon="trash" title="Remove" aria-label="Remove" loading={row.removing} onclick="fez.remove('{row.ref}')"></ui-button>
</div>
{#if row.error}<span class="cf-sub-error">The last fetch failed: {row.error}. Its events stay as they were.</span>{/if}
{#if row.asking}
<div class="cf-ask">
<span>Remove this subscription? Its events leave the calendar.</span>
<div class="cf-actions">
<ui-button size="sm" label="Cancel" onclick="fez.unask()"></ui-button>
<ui-button size="sm" variant="danger" label="Remove" onclick="fez.remove('{row.ref}')"></ui-button>
</div>
</div>
{/if}
{/if}
</div>
{:else}
<p class="cf-hint">None yet. Subscribe to a calendar by its address: a team calendar, holidays, a sports schedule.</p>
{/each}
<form class="cf-add" onsubmit="fez.subscribe(event)" novalidate>
<ui-input name="url" label="Calendar address" placeholder="https://... or webcal://..." maxlength="2000" error={state.errors.url}></ui-input>
<div class="cf-inline">
<span class="cf-grow"><ui-input name="name" label="Name" placeholder="The calendar's own name" maxlength="100" error={state.errors.name}></ui-input></span>
<ui-button type="submit" variant="primary" icon="plus" label="Subscribe" :loading="state.busy === 'subscribe'"></ui-button>
</div>
<p class="cf-hint">Fetched now and every 30 minutes; its events are read-only and change with it.</p>
</form>
</section>

<section class="cf-section">
<h3>Files</h3>
<div class="cf-actions">
<ui-button size="sm" icon="upload-simple" label="Import .ics" loading={state.busy === 'import'} onclick="fez.pick()"></ui-button>
<ui-button size="sm" icon="download-simple" label="Download .ics" loading={state.busy === 'download'} onclick="fez.download()"></ui-button>
<input class="cf-file-input" type="file" accept=".ics,text/calendar" fez:this="fileInput" onchange="fez.chosen(event)">
</div>
{#if state.fileError}<p class="cf-error">{state.fileError}</p>{/if}
<p class="cf-hint">Import adds the events of a calendar file this calendar does not hold yet. Download saves the whole calendar as one file.</p>
</section>

<div class="dialog-actions">
<ui-button variant="primary" label="Done" onclick="Dialog.close(true)"></ui-button>
</div>
{/if}`
};
}

Fez('calendar-feeds-dialog', __fez_component_0);

export default __fez_component_0;
